import { useHomeDeliveryDate } from '@/data/Content/HomeDeliveryDate';
import { useLocalization } from '@/data/Localization';
import { ProductType } from '@/data/types/Product';
import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import {
	MATERIAL_SYMBOL_ICONS,
	OpenSansFontWeights,
} from 'shared/custom/constants/commonConstants';
import { hasLoadedLabel } from '@/components/blocks/DeliveryPickupDates/utils/hasLoadedLabel';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { StyledSkeleton } from '@/components/skeleton/components/styles/CustomSkeleton';
import {
	deliveryRowSx,
	highlightTextSx,
	iconSx,
	labelTextSx,
	mutedIconSx,
	mutedLabelTextSx,
	skeletonSx,
	textGroupSx,
} from '@/components/blocks/DeliveryPickupDates/styles/deliveryPickupDates';

type Props = {
	product: ProductType | undefined;
	physicalStoreId: string;
};

export const HomeDeliveryDateLabel: FC<Props> = ({ product, physicalStoreId }) => {
	const { Availability } = useLocalization('HDMProductDetail');
	const state = useHomeDeliveryDate(product, physicalStoreId);

	const skeleton = (
		<Box sx={deliveryRowSx}>
			<StyledSkeleton variant="rectangular" sx={skeletonSx} />
		</Box>
	);

	// Still resolving (pre-hydration, or the blocked-zip list is in flight): a
	// skeleton rather than the empty row, so no partial text is shown and nothing
	// shifts when the real label replaces it.
	if (state.status === 'pending') {
		return skeleton;
	}

	// Checked before the localization probe below, so a product that will render
	// nothing never flashes a skeleton on its way to an empty row.
	if (state.status === 'no-label') {
		return <Box sx={deliveryRowSx} />;
	}

	// The formatted date is not localized, so without this the row would paint it
	// alongside a blank prefix while the translations are still in flight.
	if (!hasLoadedLabel(Availability.labels.homeDeliveryDatePrefix)) {
		return skeleton;
	}

	if (state.status === 'blocked-zip') {
		return (
			<Box sx={deliveryRowSx} className="product-delivery">
				<DynamicIcon
					name={MATERIAL_SYMBOL_ICONS.localShipping}
					variant="rounded"
					size={18}
					sx={mutedIconSx}
				/>
				<Box sx={textGroupSx}>
					<Typography variant="bodyBaseline" sx={mutedLabelTextSx} component="span">
						{Availability.labels.homeDeliveryBlockedZip.t()}
					</Typography>
				</Box>
			</Box>
		);
	}

	if (state.status === 'not-available') {
		return (
			<Box sx={deliveryRowSx} className="product-delivery">
				<DynamicIcon
					name={MATERIAL_SYMBOL_ICONS.localShipping}
					variant="rounded"
					size={18}
					sx={mutedIconSx}
				/>
				<Box sx={textGroupSx}>
					<Typography variant="bodyBaseline" sx={mutedLabelTextSx} component="span">
						{Availability.labels.homeDeliveryNotAvailable.t()}
					</Typography>
				</Box>
			</Box>
		);
	}

	const prefix =
		state.descriptor === 'date'
			? Availability.labels.homeDeliveryDatePrefixWithDate.t()
			: Availability.labels.homeDeliveryDatePrefix.t();

	const dateLabel =
		state.descriptor === 'today'
			? Availability.labels.deliveryPickupToday.t()
			: state.descriptor === 'tomorrow'
			? Availability.labels.deliveryPickupTomorrow.t()
			: state.formatted;

	return (
		<Box sx={deliveryRowSx} className="product-delivery">
			<DynamicIcon
				name={MATERIAL_SYMBOL_ICONS.localShipping}
				variant="rounded"
				size={18}
				sx={iconSx}
			/>
			<Box sx={textGroupSx}>
				<Typography variant="bodyBaseline" sx={labelTextSx} component="span">
					{prefix}
				</Typography>{' '}
				<Typography
					className="product-delivery--date"
					variant="bodyBaseline"
					sx={highlightTextSx}
					fontWeight={OpenSansFontWeights.semibold}
					component="span"
				>
					{dateLabel}
				</Typography>
			</Box>
		</Box>
	);
};
